<template>
  <div
    class="refund-modal-container"
    role="dialog"
    aria-modal="true"
  >
    <div class="refund-modal-card text-center">
      <div class="modal-header-actions">
        <ModalCloseButton
          class="dialog-minimize-btn"
          @close="onClose"
        />
      </div>

      <!-- Status Icon Wrapper -->
      <div
        class="status-icon-wrapper mb-4"
        :class="isScheduled ? 'scheduled' : 'completed'"
      >
        <font-awesome-icon
          :icon="iconName"
          class="status-icon"
        />
      </div>

      <!-- Modal Title -->
      <h3 class="dialog-title mb-3">
        {{ statusTitle }}
      </h3>

      <!-- Description Message -->
      <p class="dialog-description mb-4">
        {{ statusDescription }}
      </p>

      <!-- Action Button -->
      <BaseButton
        block
        size="sm"
        variant="primary"
        @click="onClose"
      >
        {{ $t('refund_modal.button_close') }}
      </BaseButton>
    </div>
  </div>
</template>

<script>
  import BaseButton from '@/components/ui/BaseButton.vue'
  import ModalCloseButton from '@/components/ui/ModalCloseButton.vue'
  import { PAYMENT_METHOD_PIX, PAYMENT_METHOD_CREDIT_CARD } from '@/constants.js'

  export default {
    name: 'RefundSuccessModal',
    components: {
      BaseButton,
      ModalCloseButton
    },
    props: {
      isScheduled: {
        type: Boolean,
        default: true
      },
      paymentMethod: {
        type: [Number, String],
        default: null
      },
      isAutomatic: {
        type: Boolean,
        default: false
      },
      transactionId: {
        type: [Number, String],
        default: null
      },
      amount: {
        type: [Number, String],
        default: null
      }
    },
    emits: ['close'],
    computed: {
      isPix() {
        return this.paymentMethod === PAYMENT_METHOD_PIX
      },
      isCard() {
        return this.paymentMethod === PAYMENT_METHOD_CREDIT_CARD
      },
      statusTitle() {
        return this.isScheduled
          ? this.$t('refund_modal.title_scheduled')
          : this.$t('refund_modal.title_completed')
      },
      statusDescription() {
        if (this.isScheduled) {
          if (this.isPix) return this.$t('refund_modal.message_scheduled_pix')
          if (this.isCard) return this.$t('refund_modal.message_scheduled_card')
          return this.$t('refund_modal.message_scheduled_generic')
        }

        if (this.isPix) return this.$t('refund_modal.message_completed_pix')
        if (this.isCard) return this.$t('refund_modal.message_completed_card')
        return this.$t('refund_modal.message_completed_generic')
      },
      iconName() {
        return this.isScheduled ? 'clock' : 'check-circle'
      }
    },
    methods: {
      onClose() {
        this.$emit('close')
      }
    }
  }
</script>

<style scoped lang="scss">
  @use '@/main';

  .refund-modal-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    z-index: 10010;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    box-sizing: border-box;
  }

  .refund-modal-card {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    padding: 24px;
    width: 100%;
    max-width: 440px;
    max-height: 90vh;
    max-height: 90dvh;
    overflow-y: auto;
    position: relative;
    z-index: 10011;
    animation: modalPopIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .modal-header-actions {
    display: flex;
    justify-content: flex-end;
  }

  .dialog-minimize-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 10;
  }

  @keyframes modalPopIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(12px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  .status-icon-wrapper {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;

    &.completed {
      background: rgba(40, 167, 69, 0.1);
      color: #28a745;
    }

    &.scheduled {
      background: rgba(1, 106, 179, 0.1);
      color: main.$color-energy-blue;
    }

    .status-icon {
      font-size: 2.5rem;
    }
  }

  .dialog-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #2c3e50;
    line-height: 1.3;
  }

  .dialog-description {
    font-size: 0.95rem;
    color: #5d6d7e;
    line-height: 1.5;
  }
</style>
